import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Settings, Target, Zap, FileText, Key, ExternalLink } from 'lucide-react';
import { useApiKeys } from '../contexts/ApiKeysContext';

const AutoEvalSettings = ({ promptId, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testSets, setTestSets] = useState([]);
  const [settings, setSettings] = useState({
    enabled: false,
    test_set_id: '',
    evaluation_model: 'llama-3.1-8b-instant',
    pass_threshold: 0.7
  });

  const { 
    availableModels, 
    loading: modelsLoading, 
    getValidApiKeys, 
    hasExternalApis,
    getInternalModels,
    getExternalModels 
  } = useApiKeys();

  useEffect(() => {
    loadData();
  }, [promptId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get the prompt to find its project
      const promptResponse = await fetch(`/api/prompts/${promptId}`);
      const promptData = await promptResponse.json();
      
      // Load test sets for the project
      // const testSetsResponse = await fetch(`/api/projects/${promptData.project_id}/test-sets`);
      // const testSetsData = await testSetsResponse.json();
      // setTestSets(testSetsData);
      
    } catch (error) {
      console.error('Error loading auto-eval settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Include API keys in the save request for external models
      const apiKeys = getValidApiKeys();
      const saveData = {
        ...settings,
        apiKeys: Object.keys(apiKeys).length > 0 ? apiKeys : undefined
      };
      
      // Save settings with API keys
      await fetch(`/api/prompts/${promptId}/auto-eval-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saveData)
      });
      
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Error saving auto-eval settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const selectedTestSet = testSets.find(ts => ts.id === settings.test_set_id);
  const selectedModel = availableModels.find(m => m.id === settings.evaluation_model);
  const isExternalModel = selectedModel?.type === 'external';
  const internalModels = getInternalModels();
  const externalModels = getExternalModels();

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl bg-white rounded-lg shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            Auto-Evaluation Settings
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading || modelsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading settings...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Enable Auto-Evaluation</h4>
                <p className="text-sm text-gray-500">
                  Automatically evaluate prompt outputs when logged via SDK
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {settings.enabled && (
              <>
                {/* Test Set Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Target className="h-4 w-4 inline mr-1" />
                    Test Set
                  </label>
                  <select
                    value={settings.test_set_id}
                    onChange={(e) => setSettings(prev => ({ ...prev, test_set_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select a test set...</option>
                    {testSets.map((testSet) => (
                      <option key={testSet.id} value={testSet.id}>
                        {testSet.name} ({testSet.test_case_count} test cases)
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose which test set to use for automatic evaluation
                  </p>
                </div>

                {/* Evaluation Model */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Zap className="h-4 w-4 inline mr-1" />
                    Evaluation Model
                  </label>
                  
                  {/* Model Categories */}
                  <div className="space-y-3">
                    {/* Internal Models Section */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                        Internal Models (Included)
                      </h4>
                      <select
                        value={internalModels.some(m => m.id === settings.evaluation_model) ? settings.evaluation_model : ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, evaluation_model: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select internal model...</option>
                        {internalModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name} - {model.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* External Models Section */}
                    {hasExternalApis() && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide flex items-center">
                          <Key className="h-3 w-3 mr-1" />
                          External Models (Your API Keys)
                        </h4>
                        <select
                          value={externalModels.some(m => m.id === settings.evaluation_model) ? settings.evaluation_model : ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, evaluation_model: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select external model...</option>
                          {externalModels.map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.name} ({model.provider}) - {model.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* No External APIs Available */}
                    {!hasExternalApis() && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Key className="h-4 w-4 text-blue-600 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-medium text-blue-900">Want more models?</h4>
                            <p className="text-xs text-blue-800 mt-1">
                              Add your OpenAI or Anthropic API keys in User Settings to access additional models.
                            </p>
                            <button
                              onClick={() => window.open('/settings', '_blank')}
                              className="text-xs text-blue-700 hover:text-blue-800 mt-1 flex items-center font-medium"
                            >
                              Go to Settings <ExternalLink className="h-3 w-3 ml-1" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    Model used for running auto-evaluations
                    {isExternalModel && (
                      <span className="text-blue-600 ml-1">(using your API key)</span>
                    )}
                  </p>
                </div>

                {/* Pass Threshold */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <CheckCircle className="h-4 w-4 inline mr-1" />
                    Pass Threshold: {Math.round(settings.pass_threshold * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.pass_threshold}
                    onChange={(e) => setSettings(prev => ({ ...prev, pass_threshold: parseFloat(e.target.value) }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum score required for a test case to pass
                  </p>
                </div>

                {/* Configuration Summary */}
                {selectedTestSet && selectedModel && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">
                      <Settings className="h-4 w-4 inline mr-1" />
                      Configuration Summary
                    </h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>• Test Set: <strong>{selectedTestSet.name}</strong> ({selectedTestSet.test_case_count} test cases)</p>
                      <p>• Model: <strong>{selectedModel.name}</strong> 
                        {selectedModel.provider && ` (${selectedModel.provider})`}
                        {isExternalModel && <span className="text-blue-600"> - Using your API key</span>}
                      </p>
                      <p>• Pass Threshold: <strong>{Math.round(settings.pass_threshold * 100)}%</strong></p>
                      <p>• When SDK logs are created, they will be automatically evaluated against this test set</p>
                    </div>
                  </div>
                )}

                {/* External Model Warning */}
                {isExternalModel && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-amber-900">External API Usage</h4>
                        <p className="text-xs text-amber-800 mt-1">
                          This will use your {selectedModel?.provider} API key for evaluations. 
                          Make sure your key has sufficient credits and permissions.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || (settings.enabled && (!settings.test_set_id || !settings.evaluation_model))}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoEvalSettings; 